import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation, useQuery } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import {
  getCampContactDetailsAction,
  submitCampContactDetailsAction,
  getResortsAction
} from '../../../../../services/request';

const POSITION_CHOICES = [
  { value: 'MANAGER', label: 'Kierownik' },
  { value: 'NURSE', label: 'Pielęgniarka' },
  { value: 'SUPERVISOR', label: 'Wychowawca' }
];

const POSITION_COLOR = {
  MANAGER: 'primary',
  NURSE: 'success',
  SUPERVISOR: 'warning'
};

const ContactDetailsForm = ({ campId, initial, resortOptions, onSaved, onCancel }) => {
  const { loading, mutate } = useMutation(submitCampContactDetailsAction);
  const [alert, setAlert] = React.useState(null);

  const validationSchema = Yup.object({
    name: Yup.string().required('Pole wymagane'),
    position: Yup.string().required('Pole wymagane'),
    email: Yup.string().email('Niepoprawny adres email')
      .nullable()
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initial
      ? {
        name: initial.name,
        phone: initial.phone || '',
        email: initial.email || '',
        position: initial.position,
        resorts: initial.resorts || [],
        camps: initial.camps || []
      }
      : { name: '', phone: '', email: '', position: '', resorts: [], camps: [campId] },
    validationSchema,
    onSubmit: async values => {
      const camps = _.uniq([...values.camps, campId]);
      const body = { ...values, camps };
      const { error, payload } = await mutate(body, initial ? 'PUT' : 'POST', initial?.id);
      if (!error) {
        onSaved(payload, Boolean(initial));
      } else {
        const detail = payload?.non_field_errors?.[0] || payload?.detail || 'Nie udało się zapisać.';
        setAlert({ severity: 'error', title: 'Błąd', message: detail });
      }
    }
  });

  return (
    <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1 }}>
      {alert && (
        <AlertDialogContainer
          severity={alert.severity}
          title={alert.title}
          message={alert.message}
          setShowAlert={() => setAlert(null)}
        />
      )}
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ minWidth: 110 }}>Stanowisko</Typography>
          <SmallFormTextField
            select
            id="position"
            name="position"
            options={POSITION_CHOICES}
            value={formik.values.position}
            onChange={formik.handleChange}
            error={formik.touched.position && Boolean(formik.errors.position)}
            helperText={formik.touched.position && formik.errors.position}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ minWidth: 110 }}>Ośrodki</Typography>
          <SmallFormTextField
            select
            multiple
            id="resorts"
            name="resorts"
            options={resortOptions}
            value={formik.values.resorts}
            onChange={formik.handleChange}
            renderValue={selected => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map(val => {
                  const opt = resortOptions.find(o => o.value === val);

                  return <Chip key={val} label={opt?.label || val} size="small" />;
                })}
              </Box>
            )}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ minWidth: 110 }}>Imię i nazwisko</Typography>
          <DebounceInput
            name="name"
            id="name"
            placeholder="Imię i nazwisko"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            debounceTimeout={100}
            element={SmallFormTextField}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ minWidth: 110 }}>Telefon</Typography>
          <DebounceInput
            name="phone"
            id="phone"
            placeholder="Nr telefonu"
            value={formik.values.phone}
            onChange={formik.handleChange}
            debounceTimeout={100}
            element={SmallFormTextField}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ minWidth: 110 }}>Email</Typography>
          <DebounceInput
            name="email"
            id="email"
            placeholder="Adres email"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            debounceTimeout={100}
            element={SmallFormTextField}
          />
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" size="small" onClick={onCancel} startIcon={<CloseIcon />}>
            Anuluj
          </Button>
          <Button variant="contained" size="small" type="submit" disabled={loading} startIcon={<AddIcon />}>
            Zapisz
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

const ContactDetailsRow = ({ item, onEdit, onUnlink }) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1 }}>
    <Chip
      label={item.position_display}
      color={POSITION_COLOR[item.position] || 'default'}
      size="small"
      sx={{ minWidth: 110 }}
    />
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
      <Typography variant="caption" color="text.secondary">
        {item.resorts_display?.map(r => r.name).join(', ')}
        {item.phone ? ` · ${item.phone}` : ''}
        {item.email ? ` · ${item.email}` : ''}
      </Typography>
    </Box>
    <IconButton size="small" title="Edytuj" onClick={() => onEdit(item)}><EditIcon fontSize="small" /></IconButton>
    <IconButton size="small" color="warning" title="Odepnij od turnusu" onClick={() => onUnlink(item)}><LinkOffIcon fontSize="small" /></IconButton>
  </Stack>
);

const SearchAndLinkPanel = ({ campId, currentIds, onLinked, onCancel }) => {
  const [search, setSearch] = React.useState('');
  const { loading, payload } = useQuery(getCampContactDetailsAction(null, search));
  const { mutate } = useMutation(submitCampContactDetailsAction);
  const [alert, setAlert] = React.useState(null);

  const available = React.useMemo(() => {
    const all = payload?.results || (Array.isArray(payload) ? payload : []);

    return all.filter(c => !currentIds.includes(c.id));
  }, [payload, currentIds]);

  const handleLink = async contact => {
    const updatedCamps = _.uniq([...contact.camps, campId]);
    const { error, payload: updated } = await mutate({ camps: updatedCamps, resorts: contact.resorts }, 'PATCH', contact.id);
    if (!error) {
      onLinked(updated);
    } else {
      setAlert({ severity: 'error', title: 'Błąd', message: 'Nie udało się przypiąć kontaktu.' });
    }
  };

  return (
    <Box>
      {alert && (
        <AlertDialogContainer severity={alert.severity} title={alert.title} message={alert.message} setShowAlert={() => setAlert(null)} />
      )}
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <SmallFormTextField
          placeholder="Szukaj po nazwisku..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, width: '100%' }}
        />
        <Button size="small" variant="outlined" startIcon={<CloseIcon />} onClick={onCancel}>Anuluj</Button>
      </Stack>
      {loading
        ? <CircularProgress size={20} />
        : available.length === 0
          ? <Typography variant="body2" color="text.secondary">Brak wyników.</Typography>
          : available.map(c => (
            <Stack key={c.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
              <Chip label={c.position_display} size="small" color={POSITION_COLOR[c.position] || 'default'} sx={{ minWidth: 100 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">{c.name}</Typography>
                <Typography variant="caption" color="text.secondary">{c.email}</Typography>
              </Box>
              <IconButton size="small" color="primary" title="Przypisz do turnusu" onClick={() => handleLink(c)}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))
      }
    </Box>
  );
};

export const CampContactDetailsSection = ({ campId }) => {
  const [formOpen, setFormOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [editItem, setEditItem] = React.useState(null);
  const [records, setRecords] = React.useState([]);
  const [alert, setAlert] = React.useState(null);

  const { loading, payload, error } = useQuery(getCampContactDetailsAction(campId));
  const resortsRequest = useQuery(getResortsAction(null, 'GET'));
  const { mutate: patchContact } = useMutation(submitCampContactDetailsAction);

  React.useEffect(() => {
    if (!loading && !error && payload) {
      setRecords(payload.results || (Array.isArray(payload) ? payload : []));
    }
  }, [loading]);

  const resortOptions = React.useMemo(
    () => _.map(resortsRequest.payload?.results || [], r => ({ value: r.id, label: r.name })),
    [resortsRequest.loading]
  );

  const handleSaved = (item, isEdit) => {
    setRecords(prev => isEdit ? prev.map(r => (r.id === item.id ? item : r)) : [...prev, item]);
    setFormOpen(false);
    setEditItem(null);
  };

  const handleEdit = item => {
    setEditItem(item);
    setFormOpen(true);
    setSearchOpen(false);
  };

  const handleUnlink = async item => {
    const updatedCamps = item.camps.filter(c => c !== campId);
    const { error: patchError, payload: updated } = await patchContact(
      { camps: updatedCamps, resorts: item.resorts },
      'PATCH',
      item.id
    );
    if (!patchError) {
      setRecords(prev => prev.filter(r => r.id !== item.id));
    } else {
      setAlert({ severity: 'error', title: 'Błąd', message: 'Nie udało się odpiąć kontaktu.' });
    }
  };

  const handleLinked = item => {
    setRecords(prev => [...prev, item]);
    setSearchOpen(false);
  };

  const currentIds = records.map(r => r.id);
  const isSubformOpen = formOpen || searchOpen;

  return (
    <Card>
      <CardHeader
        title="Dane kontaktowe"
        sx={{ mt: -1, mb: -2 }}
        action={
          !isSubformOpen && (
            <Stack direction="row" spacing={1}>
              <Button size="small" startIcon={<LinkIcon />} onClick={() => {
                setSearchOpen(true);
                setFormOpen(false);
              }}>
                Przypisz istniejący
              </Button>
              <Button size="small" startIcon={<AddIcon />} onClick={() => {
                setEditItem(null);
                setFormOpen(true);
                setSearchOpen(false);
              }}>
                Nowy
              </Button>
            </Stack>
          )
        }
      />
      <CardContent>
        {alert && (
          <AlertDialogContainer
            severity={alert.severity}
            title={alert.title}
            message={alert.message}
            setShowAlert={() => setAlert(null)}
          />
        )}

        {loading
          ? <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
          : records.length === 0 && !isSubformOpen
            ? <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Brak przypisanych danych kontaktowych.</Typography>
            : records.map((item, i) => (
              <React.Fragment key={item.id}>
                {i > 0 && <Divider />}
                <ContactDetailsRow item={item} onEdit={handleEdit} onUnlink={handleUnlink} />
              </React.Fragment>
            ))
        }

        {formOpen && (
          <>
            {records.length > 0 && <Divider sx={{ my: 1 }} />}
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
              {editItem ? 'Edytuj dane kontaktowe' : 'Nowe dane kontaktowe'}
            </Typography>
            <ContactDetailsForm
              campId={campId}
              initial={editItem}
              resortOptions={resortOptions}
              onSaved={handleSaved}
              onCancel={() => {
                setFormOpen(false);
                setEditItem(null);
              }}
            />
          </>
        )}

        {searchOpen && (
          <>
            {records.length > 0 && <Divider sx={{ my: 1 }} />}
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Wyszukaj i przypisz</Typography>
            <SearchAndLinkPanel
              campId={campId}
              currentIds={currentIds}
              onLinked={handleLinked}
              onCancel={() => setSearchOpen(false)}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
